// GroundingEvidence — the inspection end of grounding, pure. Composes Popover.
//
// .claude/research/design/04: expanding a claim shows the exact source quote, verbatim, with
// title, locator, and Jump to source. The quote IS the proof — no numeric
// confidence anywhere. Multiple sources stack as separate anchors, never a
// count. Degrades gracefully (blob-degradation principle): a moved source still
// shows its stored quote with a quiet "source moved" note and no jump, never a
// crash and never a false red.
//
// The trigger is the GroundingChip wrapped in a button (the chip is the preview,
// this panel the inspection — same three-state vocabulary). The panel body is a
// match over the state: grounded/weak list their anchors; ungrounded is an
// honest call to link a source, in a calm tone (not an alarm). Each anchor is a
// match over its availability so "source moved" is a state, not an error path.
//
// Pure/props-driven: state + callbacks in, requests out. `onJump` fires the
// same jump the editor uses (.claude/research/design/04: a measurement is only actionable
// if it takes you to the thing). `open`/`onOpenChange` are controlled config;
// no buried effects.

import { Trans, useLingui } from "@lingui/react/macro";
import { Button } from "../../Button/Button.js";
import { Mono } from "../../Mono/Mono.js";
import { Popover } from "../../Popover/Popover.js";
import { Text } from "../../Text/Text.js";
import { GroundingChip } from "../GroundingChip/GroundingChip.js";
import type { Anchors, GroundingState, SourceAnchor } from "../types.js";
import "./GroundingEvidence.css";

export type GroundingEvidenceProps = {
    readonly state: GroundingState;
    // Jump to the source fragment behind a resolved anchor (index within the
    // element's anchors). The composing shell owns the navigation.
    readonly onJump?: (anchorIndex: number) => void;
    // Offered only for the ungrounded state — a calm affordance to link a source.
    readonly onLinkSource?: () => void;
    readonly open?: boolean;
    readonly onOpenChange?: (open: boolean) => void;
};

const AnchorView = ({
    anchor,
    index,
    onJump,
}: {
    anchor: SourceAnchor;
    index: number;
    onJump?: (i: number) => void;
}) => (
    <figure className="bilda-grounding-evidence__anchor">
        {/* The verbatim excerpt, set in the reading face — this is the proof. */}
        <blockquote className="bilda-grounding-evidence__quote">
            {anchor.quote}
        </blockquote>
        <figcaption className="bilda-grounding-evidence__cite">
            <Text size="sm" weight="medium">
                {anchor.sourceTitle}
            </Text>
            {/* Locator is machine data → mono, never a bare number. */}
            <Mono tone="dim">{anchor.locator}</Mono>
            {anchor.type === "resolved" ? (
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onJump?.(index)}
                >
                    <Trans>Jump to source</Trans>
                </Button>
            ) : (
                // Graceful degradation: the bytes are gone; show the stored
                // quote with a quiet note, offer no jump, never a false red.
                <Text size="sm" tone="faint">
                    <Trans>Source moved</Trans>
                </Text>
            )}
        </figcaption>
    </figure>
);

const AnchorList = ({
    anchors,
    onJump,
}: {
    anchors: Anchors;
    onJump?: (i: number) => void;
}) => (
    <>
        {anchors.map((anchor, i) => (
            <AnchorView
                key={`${anchor.sourceTitle}-${anchor.locator}`}
                anchor={anchor}
                index={i}
                onJump={onJump}
            />
        ))}
    </>
);

export const GroundingEvidence = ({
    state,
    onJump,
    onLinkSource,
    open,
    onOpenChange,
}: GroundingEvidenceProps) => {
    const { t } = useLingui();
    // The chip, made interactive: a button whose accessible name says what it
    // opens (the label carries meaning; the chip's tint reinforces).
    const trigger = (
        <button
            type="button"
            className="bilda-grounding-evidence__trigger"
            aria-label={t`Show the evidence for this claim`}
        >
            <GroundingChip state={state} />
        </button>
    );

    return (
        <Popover
            trigger={trigger}
            label={t`Evidence for this claim`}
            side="bottom"
            align="start"
            open={open}
            onOpenChange={onOpenChange}
        >
            <div className="bilda-grounding-evidence">
                {state.type === "unsupported" ? (
                    <div className="bilda-grounding-evidence__empty">
                        <Text size="sm" tone="dim">
                            <Trans>
                                No claim in the knowledge bank backs this yet.
                            </Trans>
                        </Text>
                    </div>
                ) : state.type === "ungrounded" ? (
                    <div className="bilda-grounding-evidence__empty">
                        <Text size="sm" tone="dim">
                            <Trans>
                                No source is linked to this claim yet.
                            </Trans>
                        </Text>
                        {onLinkSource ? (
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={onLinkSource}
                            >
                                <Trans>Link a source</Trans>
                            </Button>
                        ) : null}
                    </div>
                ) : (
                    <AnchorList anchors={state.anchors} onJump={onJump} />
                )}
            </div>
        </Popover>
    );
};
